import React, { useEffect, useState } from "react";
import { Printer, Truck, CheckCircle2, AlertTriangle, Package } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PageTitle } from "./shared";
import { LOGOS } from "../../lib/brand";

const BOXES = ["SMALL", "MEDIUM", "LARGE", "XL"];
const CHECKLIST = [
  { key: "items_packed", label: "ALL ITEMS PACKED" },
  { key: "tags_included", label: "PRINTED TAGS INCLUDED" },
  { key: "care_card", label: "CARE CARD / GROWING NOTES" },
  { key: "packing_slip", label: "PACKING SLIP IN BOX" },
  { key: "address_label", label: "ADDRESS LABEL ON BOX" },
];

const PrintStatusChip = ({ status }) => {
  if (!status) return <span className="px-3 py-1 rounded-full bg-plum-deep/10 text-plum-deep/60 text-[10px] uppercase font-bold tracking-[0.1em]">NO PRINT JOB</span>;
  const ready = status === "ready" || status === "completed";
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase font-bold tracking-[0.1em] ${ready ? "bg-forest/15 text-forest" : "bg-orange/15 text-orange"}`} data-testid="packing-print-status">
      {ready ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      TAGS: {status.replace("_", " ")}
    </span>
  );
};

const Slip = ({ entry }) => {
  const { order, kind } = entry;
  const number = kind === "heritage" ? order.job_number : order.order_number;
  return (
    <div className="print-slip hidden print:block bg-white text-black p-10">
      <div className="flex items-center gap-4 border-b-2 border-black pb-4">
        <img src={LOGOS.bloomFoundry} alt="" style={{ width: 64, height: 64, objectFit: "contain" }} />
        <div>
          <p className="text-2xl font-bold uppercase" style={{ fontFamily: "'Cormorant Garamond', serif" }}>THE BLOOM FOUNDRY</p>
          <p className="text-xs uppercase tracking-widest">PACKING SLIP — {number}</p>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-8 text-sm">
        <div>
          <p className="font-bold uppercase text-xs mb-1">DELIVER TO</p>
          <p>{order.customer?.name}</p>
          <p>{order.customer?.address}</p>
          <p>{order.customer?.phone}</p>
          <p>{order.customer?.email}</p>
        </div>
        <div>
          <p className="font-bold uppercase text-xs mb-1">ORDER</p>
          <p>{number}</p>
          <p>{new Date(order.created_at).toLocaleDateString("en-NZ")}</p>
          <p className="uppercase">{kind === "heritage" ? "HERITAGE DAHLIAS" : "THE ETCHERY"}</p>
        </div>
      </div>
      <table className="w-full mt-6 text-sm border-collapse">
        <thead>
          <tr className="border-b border-black text-left">
            <th className="py-2 uppercase text-xs">ITEM</th>
            <th className="py-2 uppercase text-xs">DETAILS</th>
            <th className="py-2 uppercase text-xs text-right">QTY</th>
          </tr>
        </thead>
        <tbody>
          {order.items?.map((it, i) => (
            <tr key={i} className="border-b border-gray-300">
              <td className="py-2">{it.variety_name || `"${it.text_line1}"`}</td>
              <td className="py-2">{it.variety_name ? (it.type || "").toUpperCase() : `${it.shape_name} · ${it.colour_name}${it.text_line2 ? ` · ${it.text_line2}` : ""}`}</td>
              <td className="py-2 text-right">{it.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {order.notes && <p className="mt-4 text-sm"><span className="font-bold uppercase text-xs">NOTES: </span>{order.notes}</p>}
      <p className="mt-8 text-xs uppercase tracking-widest text-center">GROWN & MADE WITH LOVE IN NEW ZEALAND — THEBLOOMFOUNDRY.NZ</p>
    </div>
  );
};

const PackingAssistant = () => {
  const [entries, setEntries] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [tracking, setTracking] = useState("");

  useEffect(() => {
    api.get("/admin/packing").then((r) => {
      setEntries(r.data);
      setSelectedId(r.data[0]?.order.id || null);
    }).catch(() => setEntries([]));
  }, []);

  const selected = entries?.find((e) => e.order.id === selectedId);

  const savePacking = async (entry, packing) => {
    setEntries((l) => l.map((e) => (e.order.id === entry.order.id ? { ...e, order: { ...e.order, packing } } : e)));
    await api.patch(`/admin/packing/${entry.kind}/${entry.order.id}`, { packing });
  };

  const toggleCheck = (entry, key) => {
    const packing = { ...(entry.order.packing || {}) };
    packing.checklist = { ...(packing.checklist || {}), [key]: !(packing.checklist || {})[key] };
    savePacking(entry, packing);
  };

  const toggleItem = (entry, idx) => {
    const packing = { ...(entry.order.packing || {}) };
    packing.items = { ...(packing.items || {}), [idx]: !(packing.items || {})[idx] };
    savePacking(entry, packing);
  };

  const setBox = (entry, box) => savePacking(entry, { ...(entry.order.packing || {}), box });

  const markShipped = async (entry) => {
    const number = entry.kind === "heritage" ? entry.order.job_number : entry.order.order_number;
    const url = entry.kind === "heritage" ? `/admin/heritage-orders/${entry.order.id}` : `/admin/orders/${entry.order.id}`;
    await api.patch(url, { status: "shipped", ...(tracking.trim() ? { tracking_number: tracking.trim() } : {}) });
    setEntries((l) => l.filter((e) => e.order.id !== entry.order.id));
    setSelectedId(null);
    setTracking("");
    toast.success(`${number} marked as shipped 🚚${tracking.trim() ? " — tracking emailed to customer" : ""}`);
  };

  if (entries === null) return <p className="text-plum-deep/60">Loading packing bench…</p>;

  return (
    <div data-testid="console-packing">
      <PageTitle kicker="SHARED ADMIN · SHIPPING CENTRE" title="PACKING ASSISTANT" />

      {entries.length === 0 ? (
        <p className="text-plum-deep/60" data-testid="packing-empty">Nothing waiting to be packed — enjoy the sunshine! 🌼</p>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-2" data-testid="packing-order-list">
            {entries.map((e) => {
              const number = e.kind === "heritage" ? e.order.job_number : e.order.order_number;
              const done = Object.values(e.order.packing?.checklist || {}).filter(Boolean).length;
              return (
                <button
                  key={e.order.id}
                  onClick={() => setSelectedId(e.order.id)}
                  data-testid={`packing-select-${number}`}
                  className={`w-full text-left bg-warmwhite border rounded-[1.25rem] px-5 py-4 transition-colors ${selectedId === e.order.id ? "border-magenta shadow-[0_10px_25px_-18px_rgba(236,38,117,0.8)]" : "border-plum-deep/10 hover:border-magenta/40"}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{number}</span>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full uppercase font-bold ${e.kind === "heritage" ? "bg-forest/15 text-forest" : "bg-plum/15 text-plum"}`}>
                      {e.kind === "heritage" ? "HERITAGE" : "ETCHERY"}
                    </span>
                    <span className="ml-auto text-[10px] text-plum-deep/50 font-semibold">{done}/{CHECKLIST.length} ✓</span>
                  </div>
                  <p className="text-xs text-plum-deep/60 truncate mt-1">{e.order.customer?.name}</p>
                </button>
              );
            })}
          </div>

          {selected && (
            <div className="lg:col-span-8 bg-warmwhite border border-plum-deep/10 rounded-[1.5rem] p-6 md:p-8" data-testid="packing-detail">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-serif-display uppercase text-plum-deep text-2xl">
                  {selected.kind === "heritage" ? selected.order.job_number : selected.order.order_number}
                </h2>
                <PrintStatusChip status={selected.print_status} />
              </div>

              <div className="mt-4 grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">DELIVER TO</p>
                  <p className="text-sm font-medium">{selected.order.customer?.name}</p>
                  {selected.order.customer?.address ? <p className="text-sm text-plum-deep/70">{selected.order.customer.address}</p> : <p className="text-xs text-orange">No address on file!</p>}
                  {selected.order.customer?.phone && <p className="text-sm text-plum-deep/70">{selected.order.customer.phone}</p>}

                  <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2 mt-5">IN THE BOX</p>
                  <ul className="space-y-2">
                    {selected.order.items?.map((it, i) => (
                      <li key={i}>
                        <label className="flex items-center gap-3 bg-cream rounded-xl px-3.5 py-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(selected.order.packing?.items?.[i])}
                            onChange={() => toggleItem(selected, i)}
                            data-testid={`packing-item-check-${i}`}
                            className="w-4 h-4 accent-[#EC2675]"
                          />
                          <span className="flex-1 text-sm">
                            {it.variety_name || `"${it.text_line1}"`}
                            <span className="block text-[10px] uppercase tracking-[0.1em] text-plum-deep/50 font-semibold">
                              {it.variety_name ? it.type : `${it.shape_name}${it.size ? ` ${it.size}` : ""} · ${it.colour_name}`}
                            </span>
                          </span>
                          <span className="text-sm text-plum-deep/70">×{it.quantity}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2">BOX SIZE</p>
                  <div className="flex flex-wrap gap-2">
                    {BOXES.map((b) => (
                      <button key={b} onClick={() => setBox(selected, b)} data-testid={`packing-box-${b.toLowerCase()}`} className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.12em] font-semibold transition-colors ${selected.order.packing?.box === b ? "bg-plum text-cream" : "bg-cream border border-plum-deep/15 hover:border-magenta"}`}>
                        <Package className="w-3 h-3" /> {b}
                      </button>
                    ))}
                  </div>

                  <p className="text-[11px] uppercase tracking-[0.18em] text-plum-deep/60 font-semibold mb-2 mt-5">PACKING CHECKLIST</p>
                  <ul className="space-y-2">
                    {CHECKLIST.map((c) => (
                      <li key={c.key}>
                        <label className="flex items-center gap-3 bg-cream rounded-xl px-3.5 py-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(selected.order.packing?.checklist?.[c.key])}
                            onChange={() => toggleCheck(selected, c.key)}
                            data-testid={`packing-check-${c.key}`}
                            className="w-4 h-4 accent-[#1F8A50]"
                          />
                          <span className="text-sm">{c.label}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <input
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  placeholder="Tracking number (optional)"
                  data-testid="packing-tracking-number"
                  className="rounded-full px-5 py-3 bg-cream border border-plum-deep/15 text-sm focus:outline-none focus:border-magenta w-64"
                />
                <button onClick={() => window.print()} data-testid="packing-print-slip" className="inline-flex items-center gap-2 rounded-full bg-cream border border-plum-deep/20 text-plum-deep px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:border-magenta">
                  <Printer className="w-4 h-4" /> PRINT PACKING SLIP
                </button>
                <button onClick={() => markShipped(selected)} data-testid="packing-mark-shipped" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-6 py-3 text-xs uppercase tracking-[0.16em] font-semibold hover:bg-plum">
                  <Truck className="w-4 h-4" /> MARK AS SHIPPED
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {selected && <Slip entry={selected} />}
    </div>
  );
};

export default PackingAssistant;
